import { useQuery } from "@octopusdeploy/octopus-react-client";
import type { LicenseStatusResource, LicenseUsageResource } from "@octopusdeploy/octopus-server-client";
import { optionalStringQueryParam, useQueryStringParam } from "@octopusdeploy/portal-routes";
import cn from "classnames";
import * as React from "react";
import type { AnalyticActionDispatcher } from "~/analytics/Analytics";
import { Action, useAnalyticActionDispatch } from "~/analytics/Analytics";
import { CategoryButton } from "~/components/CategoryButton/CategoryButton";
import FeatureToggleVisibility from "~/components/FeatureToggle/New/FeatureToggleVisibility";
import PageDivider from "~/components/PageDivider";
import { Section } from "~/components/Section/Section";
import { orderBy } from "~/utils/orderBy";
import styles from "./EndpointSelector.module.less";
import type { CategorizedEndpointRegistration, CategorizedEndpointResult, CategoryDefinition, EndpointRegistration } from "./endpointRegistry";
import { EndpointSelectionScope } from "./endpointRegistry";
interface DeploymentTargetLimitsContextValue {
    licenseUsage: LicenseUsageResource | null;
    licenseStatus: LicenseStatusResource | null;
}
const DeploymentTargetLimitsContext = React.createContext<DeploymentTargetLimitsContextValue | undefined>(undefined);
export const useDeploymentTargetLimitsContext = (): DeploymentTargetLimitsContextValue => {
    const context = React.useContext(DeploymentTargetLimitsContext);
    if (context === undefined) {
        throw new Error("useDeploymentTargetLimitsContext must be used within a DeploymentTargetLimitsProvider");
    }
    return context;
};
type EndpointSelectorProps = {
    spaceId: string;
    onSelect: (registration: EndpointRegistration, category: CategoryDefinition) => void;
    scope: EndpointSelectionScope;
    registrations: EndpointRegistration[];
    categories: CategoriesLookup;
    defaultCategory: string;
    heading: React.ReactNode;
    showUpsellExperience: boolean;
};
export const EndpointSelector: React.FC<EndpointSelectorProps> = ({ spaceId, registrations, categories, defaultCategory, heading, scope, showUpsellExperience, ...rest }) => {
    const [selectedCategory, setSelectedCategory] = useSelectedCategoryFromQueryString(categories, defaultCategory);
    const dispatchAction = useAnalyticActionDispatch();
    const categoryButtons: React.ReactNode[] = orderBy(Object.values(categories).map((x) => ({ label: x.category.category, displayOrder: x.category.displayOrder, action: () => setSelectedCategory(x.category) })), ["displayOrder", "label"]).map((x) => <CategoryButton label={x.label} onClick={x.action} key={x.label} isActive={selectedCategory?.category === x.label}/>);
    return (<React.Fragment>
            <Section>{heading}</Section>
            <CategoryButtons className={styles.paperActions}>{categoryButtons}</CategoryButtons>
            <Section bodyClassName={styles.container}>
                {selectedCategory && <EndpointCardGroupTitle className={styles.activeItemGroupHeading}>{selectedCategory.title}</EndpointCardGroupTitle>}
                {selectedCategory && !!selectedCategory.help && <EndpointCardGroupHelp>{selectedCategory.help}</EndpointCardGroupHelp>}
                {selectedCategory && (<EndpointCardGroup spaceId={spaceId} scope={scope} endpoints={categories[selectedCategory.category] && categories[selectedCategory.category].endpoints} onSelect={rest.onSelect} category={categories[selectedCategory.category] && categories[selectedCategory.category].category} dispatchAction={dispatchAction} showUpsellExperience={showUpsellExperience}/>)}
            </Section>
        </React.Fragment>);
};
EndpointSelector.displayName = "EndpointSelector"
interface EndpointCardGroupProps {
    spaceId: string;
    endpoints: CategorizedEndpointRegistration[];
    className?: string;
    category: CategoryDefinition;
    scope: EndpointSelectionScope;
    onSelect: (registration: CategorizedEndpointRegistration, category: CategoryDefinition) => void;
    dispatchAction: AnalyticActionDispatcher;
    showUpsellExperience: boolean;
}
const EndpointCardGroup: React.FC<EndpointCardGroupProps> = ({ spaceId, endpoints = [], className, onSelect, category, scope, dispatchAction, showUpsellExperience }) => {
    const { result: licenseUsage } = useQuery(async (repository) => (showUpsellExperience ? await repository.Licenses.getCurrentUsage() : null), [showUpsellExperience], "Load license usage for deployment targets");
    const { result: licenseStatus } = useQuery(async (repository) => (showUpsellExperience ? await repository.Licenses.getCurrentStatus() : null), [showUpsellExperience], "Load license status for deployment targets");
    const orderedEndpoints = orderBy(endpoints, ["displayOrder", "name"]);
    return (<DeploymentTargetLimitsContext.Provider value={{ licenseUsage, licenseStatus }}>
            <div className={cn(styles.cardGroup, className)}>
                {orderedEndpoints.map((endpoint) => (<EndpointCard key={endpoint.name} spaceId={spaceId} endpoint={endpoint} onSelect={onSelect} category={category} scope={scope} dispatchAction={dispatchAction} showUpsellExperience={showUpsellExperience}/>))}
            </div>
        </DeploymentTargetLimitsContext.Provider>);
};
EndpointCardGroup.displayName = "EndpointCardGroup"
export const EndpointCardGroupTitle: React.FC<{
    className?: string;
    children?: React.ReactNode;
}> = ({ className, children }) => (<div className={cn(styles.groupHeading, className)}>
        <PageDivider>{children}</PageDivider>
    </div>);
EndpointCardGroupTitle.displayName = "EndpointCardGroupTitle"
export const EndpointCardGroupHelp: React.FC<{
    className?: string;
    children?: React.ReactNode;
}> = ({ className, children }) => <div className={cn(styles.groupHelp, className)}>{children}</div>;
EndpointCardGroupHelp.displayName = "EndpointCardGroupHelp"
interface EndpointCardProps {
    spaceId: string;
    endpoint: CategorizedEndpointRegistration;
    className?: string;
    category: CategoryDefinition;
    scope: EndpointSelectionScope;
    onSelect: (registration: CategorizedEndpointRegistration, category: CategoryDefinition) => void;
    dispatchAction: AnalyticActionDispatcher;
    showUpsellExperience?: boolean;
}
const EndpointCard: React.FC<EndpointCardProps> = ({ spaceId, endpoint, onSelect, category, scope, dispatchAction, showUpsellExperience }) => {
    const onClick = () => {
        if (scope === EndpointSelectionScope.DeploymentTarget) {
            dispatchAction("Add Deployment Target", {
                resource: "Deployment Target",
                action: Action.Add,
                data: {
                    target: endpoint.name,
                },
            });
        }
    };
    const card = endpoint.renderCard({
        spaceId,
        scope,
        registration: endpoint,
        category,
        onNavigate: () => onSelect(endpoint, category),
        onClick,
        showUpsellExperience,
    });
    const clonedCard = React.cloneElement(card, { key: endpoint.name, showUpsellExperience });
    if (endpoint.visibilityFeatureToggle === undefined) {
        return clonedCard;
    }
    else {
        return (<FeatureToggleVisibility key={endpoint.name} toggle={endpoint.visibilityFeatureToggle}>
                {clonedCard}
            </FeatureToggleVisibility>);
    }
};
EndpointCard.displayName = "EndpointCard"
const CategoryButtons: React.FC<{
    className?: string;
    children?: React.ReactNode;
}> = ({ children, className }) => <div className={cn(styles.actions, className)}>{children}</div>;
CategoryButtons.displayName = "CategoryButtons"
export type CategoriesLookup = Record<string, CategorizedEndpointResult>;
const categoryParameter = optionalStringQueryParam("category");
function useSelectedCategoryFromQueryString(categories: CategoriesLookup, defaultCategory: string): [
    CategoryDefinition | undefined,
    (category: CategoryDefinition) => void
] {
    const defaultCategoryDefinition = categories[defaultCategory].category;
    const [value, setValue] = useQueryStringParam(categoryParameter);
    const setCategoryDefinition = React.useCallback((category: CategoryDefinition) => setValue(category.category), [setValue]);
    const categoryFromUrl = categories[value ?? ""];
    return [categoryFromUrl?.category ?? defaultCategoryDefinition, setCategoryDefinition];
}
